import React, { Dispatch, SetStateAction, useCallback, useState } from "react";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type ExecutableFunction<TArgs extends any[], TResult> = (...args: TArgs) => Promise<TResult>;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type ExecuteRequestFunction = ExecutableFunction<any, any> | Array<ExecutableFunction<any, any>>;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type ExecuteRequestArguments = Parameters<any>;
type ExecuteRequest = (
    fn: ExecuteRequestFunction,
    actionName: string,
    args?: ExecuteRequestArguments
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
) => Promise<any> | Array<Promise<any>>;

export interface UseRequest {
    isLoading: boolean;
    setIsLoading: (isLoading: boolean) => void;
    setUploadAbortControllers: Dispatch<SetStateAction<AbortController[]>>;
    abortUploads: () => void;
    executeRequest: ExecuteRequest;
    actionName: string;
}

export function useRequest(): UseRequest {
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [, setUploadAbortControllers] = useState<AbortController[]>([]);
    const [actionName, setActionName] = useState<string>("");
    const [, setRequestCount] = useState<number>(0);

    const increaseRequestCount = () => {
        setRequestCount(prev => prev + 1);
        setIsLoading(true);
    };

    const decreaseRequestCount = () => {
        setRequestCount(prev => {
            const newRequestCount = prev - 1;
            if (newRequestCount === 0) {
                setIsLoading(false);
            }
            return newRequestCount;
        });
    };

    const abortUploads = () => {
        setUploadAbortControllers(abortControllers => {
            abortControllers.forEach(abortController => {
                abortController.abort();
            });
            return [];
        });
    };

    // TODO: Pay attention actionName is overwritten every call. Implement a heap of all running actions in next refactor.
    const executeRequest = useCallback(async (
        fn: ExecuteRequestFunction,
        requestName: string,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        args: Parameters<any> = []
    ) => {
        let result;
        try {
            increaseRequestCount();
            setActionName(requestName);
            if (Array.isArray(fn)) {
                result = await Promise.all(
                    fn.map((f, i) => {
                        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                        // @ts-ignore
                        return f.call(null, ...args[i]);
                    })
                );
            } else {
                result = await fn.call(null, ...args);
            }
        } finally {
            decreaseRequestCount();
            setActionName("");
        }
        return result;
    }, []);

    return { actionName, isLoading, setIsLoading, setUploadAbortControllers, abortUploads, executeRequest };
}

export const RequestContext = React.createContext<UseRequest>({
    isLoading: false,
    setIsLoading: () => null,
    setUploadAbortControllers: () => null,
    abortUploads: () => null,
    executeRequest: () => new Promise(() => null),
    actionName: "",
});

export const RequestContextProvider = ({ children }: { children: React.ReactNode }): JSX.Element => {
    const {
        isLoading,
        setIsLoading,
        setUploadAbortControllers,
        abortUploads,
        executeRequest,
        actionName,
    } = useRequest();
    return (
        <RequestContext.Provider
            value={{ isLoading, setIsLoading, setUploadAbortControllers, abortUploads, executeRequest, actionName }}
        >
            {children}
        </RequestContext.Provider>
    );
};

export const useRequestContext = (): UseRequest => React.useContext(RequestContext);
